import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Users, UserPlus, Trophy, Flame, Scale, Dumbbell, Activity, Trash2,
  CalendarCheck, TrendingUp, ChevronLeft, Loader2, Utensils,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/dates";
import { SectionHeader, EmptyState, BmiBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Trainer() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(null);

  const { data: clients, isLoading } = useQuery({
    queryKey: ["trainer-clients"],
    queryFn: async () => (await api.get("/trainer/clients")).data,
  });

  const addClient = async () => {
    if (!code.trim()) return;
    setAdding(true);
    try {
      await api.post("/trainer/clients", { code: code.trim() });
      toast.success(t("clientAdded"));
      setCode("");
      qc.invalidateQueries({ queryKey: ["trainer-clients"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setAdding(false); }
  };

  const removeClient = async (id) => {
    await api.delete(`/trainer/clients/${id}`);
    toast.success(t("clientRemoved"));
    qc.invalidateQueries({ queryKey: ["trainer-clients"] });
  };

  if (selected) return <ClientDetail clientId={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-6">
      <SectionHeader title={t("trainer")} subtitle={t("trainerSubtitle")} />

      <div className="rounded-2xl border bg-card p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display font-semibold"><UserPlus className="h-4 w-4 text-primary" /> {t("addClient")}</h3>
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("clientCodePlaceholder")}
            className="uppercase tracking-widest" maxLength={6} data-testid="trainer-code-input"
            onKeyDown={(e) => e.key === "Enter" && addClient()} />
          <Button onClick={addClient} disabled={adding} className="gap-1.5" data-testid="trainer-add-btn">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} {t("add")}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t("clientCodeHint")}</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : clients?.length ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {clients.map((c) => (
            <div key={c.user_id} className="rounded-2xl border bg-card p-5 transition-transform hover:-translate-y-0.5 hover:border-primary/40" data-testid={`client-${c.user_id}`}>
              <div className="flex items-start justify-between">
                <button onClick={() => setSelected(c.user_id)} className="flex items-center gap-3 text-left">
                  <Avatar className="h-11 w-11"><AvatarImage src={c.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-primary font-semibold">{(c.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                  <div>
                    <div className="font-display font-semibold">{c.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{c.goal ? t(c.goal) : "—"}</div>
                  </div>
                </button>
                <button onClick={() => removeClient(c.user_id)} aria-label="Remove" className="-m-2 grid h-10 w-10 place-items-center rounded-lg text-muted-foreground hover:text-destructive" data-testid={`client-remove-${c.user_id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                <Mini icon={Flame} value={c.streak ?? 0} label={t("days")} />
                <Mini icon={Dumbbell} value={c.weekly?.sessions ?? 0} label={t("sessionsWeek")} />
                <Mini icon={Scale} value={c.weight ?? "—"} label="kg" />
                <Mini icon={Trophy} value={c.weekly?.prs?.length ?? 0} label="PR" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelected(c.user_id)} className="mt-4 w-full" data-testid={`client-view-${c.user_id}`}>{t("viewDetail")}</Button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Users} title={t("noClients")} description={t("noClientsHint")} />
      )}
    </div>
  );
}

const Mini = ({ icon: Icon, value, label }) => (
  <div className="rounded-lg border p-2">
    <Icon className="mx-auto h-3.5 w-3.5 text-muted-foreground" />
    <div className="mt-0.5 font-display text-base font-bold tabular-nums">{value}</div>
    <div className="text-[10px] text-muted-foreground">{label}</div>
  </div>
);

function ClientDetail({ clientId, onBack }) {
  const { t, lang } = useI18n();
  const { data: c, isLoading } = useQuery({
    queryKey: ["trainer-client", clientId],
    queryFn: async () => (await api.get(`/trainer/clients/${clientId}`)).data,
  });

  if (isLoading || !c) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const w = c.weekly || {};
  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="client-back-btn">
        <ChevronLeft className="h-4 w-4" /> {t("back")}
      </button>

      <div className="flex items-center gap-4 rounded-2xl border bg-card p-5">
        <Avatar className="h-16 w-16"><AvatarImage src={c.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-lg font-semibold text-primary">{(c.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
        <div>
          <div className="font-display text-2xl font-bold">{c.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="capitalize">{c.goal ? t(c.goal) : "—"}</span>
            {c.experience && <span className="capitalize">· {t(c.experience)}</span>}
          </div>
        </div>
      </div>

      {/* Current stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Scale} label={t("latestWeight")} value={c.weight ?? "—"} unit="kg" />
        <Stat icon={Activity} label={t("bmiTitle")} value={c.bmi ?? "—"}>
          {c.bmi_category && <div className="mt-1"><BmiBadge category={c.bmi_category} t={t} /></div>}
        </Stat>
        <Stat icon={Flame} label={t("currentStreak")} value={c.streak ?? 0} unit={t("days")} />
        <Stat icon={Utensils} label={t("calories")} value={c.calorie_target ?? "—"} />
      </div>

      {/* Weekly review */}
      <div className="rounded-2xl border bg-card p-5">
        <h3 className="mb-4 font-display font-semibold">{t("weeklyReview")}</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat icon={CalendarCheck} label={t("checkIn")} value={w.checkins ?? 0} border />
          <Stat icon={Flame} label={t("adherence")} value={`${w.adherence ?? 0}%`} border />
          <Stat icon={Dumbbell} label={t("volume")} value={(w.volume ?? 0).toLocaleString()} unit="kg" border />
          <Stat icon={TrendingUp} label={t("weightChange")} value={w.weight_change != null ? `${w.weight_change > 0 ? "+" : ""}${w.weight_change}kg` : "—"} border />
        </div>
      </div>

      {/* Recent sessions */}
      <div>
        <h3 className="mb-3 font-display font-semibold">{t("history")}</h3>
        {c.recent_sessions?.length ? (
          <div className="space-y-2">
            {c.recent_sessions.map((s, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border bg-card px-4 py-3" data-testid={`client-session-${i}`}>
                <div>
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(s.date, lang)}{s.prs?.length ? ` · 🏆 ${s.prs.join(", ")}` : ""}</div>
                </div>
                <div className="flex items-center gap-4 text-right text-sm">
                  <div><div className="font-display font-bold tabular-nums">{s.duration_min || 0}</div><div className="text-[11px] text-muted-foreground">min</div></div>
                  <div><div className="font-display font-bold tabular-nums">{(s.volume || 0).toLocaleString()}</div><div className="text-[11px] text-muted-foreground">kg</div></div>
                  <div><div className="font-display font-bold tabular-nums">{s.total_sets || 0}</div><div className="text-[11px] text-muted-foreground">{t("set")}</div></div>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">{t("noData")}</p>}
      </div>

      {/* Nutrition days */}
      {c.nutrition_days?.length > 0 && (
        <div>
          <h3 className="mb-3 font-display font-semibold">{t("nutrition")}</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {c.nutrition_days.map((n) => (
              <div key={n.date} className="flex items-center justify-between rounded-xl border bg-card px-4 py-2.5 text-sm">
                <span className="text-muted-foreground">{formatDate(n.date, lang)}</span>
                <span className="font-display font-bold tabular-nums">{n.calories.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">kcal</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent check-ins */}
      {c.recent_checkins?.length > 0 && (
        <div>
          <h3 className="mb-3 font-display font-semibold">{t("checkIn")}</h3>
          <div className="flex flex-wrap gap-2">
            {c.recent_checkins.map((ci, i) => (
              <div key={i} className="rounded-xl border bg-card px-3 py-2 text-xs">
                <div className="font-medium">{formatDate(ci.date, lang)}</div>
                <div className="mt-0.5 text-muted-foreground">{lang === "id" ? "Mood" : "Mood"} {ci.mood ?? "—"} · {lang === "id" ? "Energi" : "Energy"} {ci.energy ?? "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, unit, border, children }) => (
  <div className={border ? "rounded-xl border p-4" : "rounded-2xl border bg-card p-4"}>
    <Icon className="h-4 w-4 text-muted-foreground" />
    <div className="mt-2 font-display text-2xl font-bold tabular-nums">{value}{unit ? <span className="text-xs font-normal text-muted-foreground"> {unit}</span> : ""}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
    {children}
  </div>
);
